// EPIC-P05-07: Badge Table Component
// Displays paginated list of badges

import { ResponsiveTable, ResponsiveTableColumn } from '../ResponsiveTable'
import { BadgeImage } from '@/components/profile/BadgeImage'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye, Edit } from 'lucide-react'
import { BadgeListItem } from '@/lib/api/admin/badges'
import { formatDistanceToNow } from 'date-fns'

interface BadgeTableProps {
  badges: BadgeListItem[]
  onBadgeClick: (badge: BadgeListItem) => void
  isLoading?: boolean
}

const RARITY_COLORS: Record<string, string> = {
  common: 'bg-gray-100 text-gray-800',
  rare: 'bg-blue-100 text-blue-800',
  epic: 'bg-purple-100 text-purple-800',
  legendary: 'bg-yellow-100 text-yellow-800',
}

const CATEGORY_LABELS: Record<string, string> = {
  analysis: 'Analysis',
  practice: 'Practice',
  streak: 'Streak',
  milestone: 'Milestone',
  social: 'Social',
}

export function BadgeTable({ badges, onBadgeClick, isLoading }: BadgeTableProps) {
  const columns: ResponsiveTableColumn<BadgeListItem>[] = [
    {
      key: 'icon',
      header: 'Icon',
      mobileLabel: 'Badge',
      render: (badge) => (
        <div className="flex items-center gap-3">
          <BadgeImage
            category={badge.category}
            rarity={badge.rarity}
            badge_key={badge.badge_key}
            achievement_id={badge.id}
            image_path={badge.icon_path}
            size={40}
          />
          <div className="md:hidden">
            <div className="font-medium">{badge.title}</div>
            <div className="text-xs text-muted-foreground">{badge.badge_key}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (badge) => (
        <div>
          <div className="font-medium">{badge.title}</div>
          <div className="text-xs text-muted-foreground">{badge.badge_key}</div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (badge) => (
        <Badge variant="outline" className="text-xs">
          {CATEGORY_LABELS[badge.category] || badge.category}
        </Badge>
      ),
    },
    {
      key: 'rarity',
      header: 'Rarity',
      render: (badge) => (
        <Badge className={`text-xs ${RARITY_COLORS[badge.rarity] || ''}`}>
          {badge.rarity.charAt(0).toUpperCase() + badge.rarity.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'active',
      header: 'Active',
      render: (badge) => (
        <Badge variant={badge.is_active ? 'default' : 'secondary'}>
          {badge.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'updated',
      header: 'Last Updated',
      render: (badge) => (
        <div className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(badge.updated_at), { addSuffix: true })}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (badge) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onBadgeClick(badge)}
            className="h-8"
          >
            <Eye className="h-4 w-4 mr-1" />
            <span className="hidden md:inline">View</span>
          </Button>
        </div>
      ),
    },
  ]

  return (
    <ResponsiveTable
      columns={columns}
      data={badges}
      emptyMessage={isLoading ? 'Loading badges...' : 'No badges found'}
    />
  )
}

